<template>
	<div>
		<div ref="sentinelElement" />
		<slot />
	</div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, toRefs, watch } from 'vue';

/**
 * Utility for scroll-loading paginated data.
 *
 * Use default slot to provide the loading state or animation.
 */
export type AppScrollLoaderProps = {
	isLoading: boolean;
	isEndReached: boolean;

	/**
	 * A ref to the container of the list. The `AppScrollLoader` should be
	 * rendered as the *last* element in this container.
	 */
	container?: HTMLElement;
	rootMargin?: string;
};

const props = defineProps<AppScrollLoaderProps>();

const emit = defineEmits(['load-next-batch']);

const { isLoading, isEndReached } = toRefs(props);
const sentinelElement = ref<HTMLElement>();

const handleIntersect: IntersectionObserverCallback = (entries) => {
	const isIntersecting = entries.some((entry) => entry.isIntersecting);

	if (isIntersecting && !isLoading.value && !isEndReached.value) {
		emit('load-next-batch');
	}
};

const observer = new IntersectionObserver(handleIntersect, {
	root: props.container,
	rootMargin: props.rootMargin,
	threshold: [0.1],
});

const startObserving = () => {
	if (sentinelElement.value) {
		observer.observe(sentinelElement.value);
	}
};

const disconnectObserver = () => {
	observer.disconnect();
};

watch(
	() => props.isLoading,
	() => {
		if (!props.isLoading) {
			// Restart observing after loading has finished
			disconnectObserver();
			startObserving();
		}
	}
);

watch(isEndReached, () => {
	if (isEndReached.value) {
		disconnectObserver();
	} else {
		startObserving();
	}
});

onMounted(startObserving);
onUnmounted(disconnectObserver);
</script>
